import { FunctionComponent, useContext, useEffect, useState } from 'react'
import { Range, getTrackBackground } from 'react-range'
import styled, { ThemeContext, keyframes } from 'styled-components'

import Spacer from 'components/Spacer'

import { addCommas } from 'utils/stringFormatting'
import useDebounce from 'utils/useDebounce'

interface IColorProps {
  color?: string
}

const SliderFillContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
`

const DollarSignText = styled.div<IColorProps>`
  ${props => props.color && `color: ${props.color};`}
  font-size: 14px;
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-size: 24px;
  }
`

interface ILeftProps {
  left: boolean
}
interface IMaxValueProps {
  maxValue: boolean
}
interface INoBorderRadius {
  noBorderRadius?: boolean
}
interface IVestibuleWidth {
  vestibuleWidth?: number
}
const SliderFillVestibule = styled.div<
  ILeftProps & IMaxValueProps & INoBorderRadius & IVestibuleWidth
>`
  flex-shrink: 0;
  height: 3px;
  width: 120px;
  background: ${props => props.theme.colors[props.left ? 'blue' : 'grey']};
  @media (min-width: ${props => props.theme.metrics.phone}px) {
    width: ${props => props.vestibuleWidth || 15}px;
    ${props => `
      border-top-${props.left ? 'left' : 'right'}-radius: 4px;
      border-bottom-${props.left ? 'left' : 'right'}-radius: 4px;
    `}
    ${props => props.noBorderRadius && 'border-radius: 0;'}
  }
`

interface IBackgroundProps {
  background: string
}
interface IRoundedProps {
  rounded?: boolean
}
interface IHeightProps {
  height?: number
}
const SliderFill = styled.div<IBackgroundProps & IRoundedProps & IHeightProps>`
  flex-grow: 1;
  height: ${props => (props.height ? `${props.height}px` : '3px')};
  width: 100%;
  padding: 0 15px;
  background: ${props => props.background};
  ${props => props.rounded && 'border-radius: 4px;'}
`

const pulseOuter = keyframes`
  0% {
    transform: scale(0.25);
    opacity: 100%;
  }
  100% {
    transform: scale(1);
    opacity: 0%;
  }
`

const pulseInner = keyframes`
  0% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
`

interface IIsDraggedProps {
  isDragged?: boolean
} // not used but could be for styles during drag
const Slider = styled.div<IIsDraggedProps>`
  cursor: grabbing;
  outline: none;
  z-index: 2;
`

const SliderThumb = styled.div<IColorProps>`
  /* inner circle */
  width: 10px;
  height: 10px;
  top: -5px;
  position: absolute;
  border-radius: 50%;
  background: ${props => (props.color ? props.color : props.theme.colors.blue4)};

  animation-duration: 1s;
  animation-name: ${pulseInner};
  animation-iteration-count: infinite;
  animation-direction: alternate;

  &:after {
    /* outer circle */
    content: '';
    top: -20px;
    left: -20px;
    height: 50px;
    width: 50px;
    position: absolute;
    border-radius: 50%;
    background-color: ${props => props.theme.colors.transparentBlue4Alpha65};
    z-index: -1;
    animation-duration: 2s;
    animation-name: ${pulseOuter};
    animation-iteration-count: infinite;
  }

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    /* inner circle */
    width: 30px;
    height: 30px;
    top: -15px;
    left: -15px;

    /* outer circle */
    &:after {
      top: -35px;
      left: -35px;
      height: 100px;
      width: 100px;
    }
  }
`

const Popover = styled.div<IIsDraggedProps & IColorProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;

  top: -95px;
  left: -60px;
  width: 130px;
  height: 65px;

  color: ${props => props.theme.colors.white};
  border-radius: 20px;
  background-color: ${props => (props.color ? props.color : props.theme.colors.blue4)};
  padding: 10px;

  &:after {
    content: ' ';
    position: absolute;
    top: 54px;
    width: 0;
    height: 0;
    border-left: 15px solid transparent;
    border-right: 15px solid transparent;
    border-top: 30px solid ${props => (props.color ? props.color : props.theme.colors.blue4)};
  }

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    top: -150px;
    left: -115px;
    width: 230px;
    height: 100px;
    padding: 10px 30px;

    &:after {
      top: 95px;
    }
  }
`

interface IAlignItemsProps {
  alignItems?: 'center' | 'flex-start'
}
const PopoverFlexContainer = styled.div<IAlignItemsProps>`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: ${props => props.alignItems || 'center'};
`

const BigMetric = styled.div`
  font-weight: 900;
  font-size: 22px;
  color: ${props => props.theme.colors.white};
  text-shadow: 1px 1px 0px rgba(0, 0, 0, 0.07);
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-size: 36px;
  }
`

const MetricLabel = styled(BigMetric)`
  font-weight: normal;
  font-size: 12px;
  text-align: center;
  z-index: 1;
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-size: 18px;
  }
`

interface IProps {
  max: number
  metric?: string
  metricLabel?: string
  min: number
  name: string
  disabled?: boolean
  noVestibules?: boolean
  skipDebounce?: boolean
  step: number
  color?: string
  textColor?: string
  fillHeight?: number
  toFixed?: number
  value: number
  vestibuleWidth?: number
  interpolateFn?(value: number): number
  onChange(name: string, value: number): void
}

const SliderInput: FunctionComponent<IProps> = ({
  max,
  metric,
  metricLabel,
  min,
  name,
  disabled,
  noVestibules,
  skipDebounce,
  step,
  color,
  textColor,
  fillHeight,
  toFixed,
  value,
  vestibuleWidth,
  interpolateFn,
  onChange,
}) => {
  const theme = useContext(ThemeContext)

  const [initialized, setInitialized] = useState(false)
  const [newValue, setNewValue] = useState(value || 1)

  useEffect(() => {
    if (value || Number.isInteger(value)) setNewValue(value)
  }, [value])

  const debouncedValue = skipDebounce ? newValue : Number(useDebounce(newValue, 1000))

  useEffect(() => {
    if (!skipDebounce && initialized) {
      onChange(name, debouncedValue)
    }
  }, [debouncedValue])

  const handleChange = (values: number[]) => {
    if (disabled) return

    setInitialized(true)
    setNewValue(values[0])
    if (skipDebounce) onChange(name, values[0])
  }

  const throttledHandleChange = handleChange // TODO: test if throttling is needed

  const displayValue = `${(interpolateFn ? interpolateFn(newValue) : newValue).toFixed(
    toFixed || 0,
  )}${metric || ''}`

  return (
    <Range
      values={[newValue]}
      min={min}
      max={max}
      step={step}
      onChange={throttledHandleChange}
      renderTrack={({ props, children }) => (
        <SliderFillContainer
          onMouseDown={props.onMouseDown}
          onTouchStart={props.onTouchStart}
        >
          <Spacer size={10} />
          <DollarSignText color={textColor}>$</DollarSignText>
          <Spacer size={20} />
          {!noVestibules && (
            <SliderFillVestibule
              left
              maxValue={newValue >= max}
              vestibuleWidth={vestibuleWidth}
            />
          )}
          <SliderFill
            ref={props.ref}
            background={getTrackBackground({
              values: [newValue],
              colors: [color || theme.colors.blue4, theme.colors.grey5],
              min,
              max,
            })}
            height={fillHeight}
            rounded={noVestibules}
          >
            {children}
          </SliderFill>
          {!noVestibules && (
            <SliderFillVestibule
              left={false}
              maxValue={newValue >= max}
              vestibuleWidth={vestibuleWidth}
            />
          )}
          <Spacer size={20} />
          <DollarSignText color={textColor}>$$$$</DollarSignText>
        </SliderFillContainer>
      )}
      renderThumb={({ props, isDragged }) => (
        <Slider
          {...props}
          style={{ ...props.style }}
          isDragged={isDragged}
        >
          <SliderThumb color={color} />
          <Popover color={color}>
            <PopoverFlexContainer>
              <BigMetric>{`$${addCommas(displayValue)}`}</BigMetric>
              {metricLabel && <MetricLabel>{metricLabel}</MetricLabel>}
            </PopoverFlexContainer>
          </Popover>
        </Slider>
      )}
    />
  )
}

export default SliderInput
